/* eslint-disable react-hooks/rules-of-hooks */
import classNames from 'classnames';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation, useParams } from 'react-router-dom';

import ErrorBoundaryWrapper from '../../shared/components/error-boundary-wrapper';
import Suspense from '../../shared/components/suspense';
import { StorageUtil } from '../../shared/utils/storage';
import toaster, { Theme } from '../../shared/toaster';
import { dateToString } from '../../shared/functions/date';
import {
  getIsRequestSucceeded,
  redirectWithoutRefresh,
} from '../../shared/utils';
import TableLoadingSkeleton from './components/table/table-loading-skeleton';
import SearchingEmpty from './components/searching-empty';
import LeadFinderExtensionBanner from './components/lead-finder-extension-banner';
import RecentSearch from './components/recent-search/recent-search';
import NoSearchResult from './components/no-search-result/no-search-result';
import NoSearchResultIcon from '../../shared/components/images/no-search-result-icon';
import { useLeadFinder } from './components/hooks/useLeadFinder';
import { useSavedLeadFilter } from './components/hooks/useLeadSavedFilter';
import { useLeadApiState } from './components/hooks/useLeadApiState';
import { useCompanyFinder } from './components/hooks/useCompanyFinder';
import {
  LeadFinderPrimaryTab,
  LeadFinderSecondaryTab,
  SavedTabFilters,
  BulkLeadsActionType,
  LeadRevealType,
} from './types';
import {
  getV2BulkOperationLimit,
  SAVED_TAB_BULK_OPERATION_LIMIT,
  V2_LEAD_FINDER_RECENT_SEARCHES_STORAGE_KEY_COMPANY,
  V2_LEAD_FINDER_RECENT_SEARCHES_STORAGE_KEY,
  computeLeadRevealCredits,
  delay,
} from './helpers/helper';
import { RootState } from '../../store/root-reducer';
import { getTeamsRequest } from '../settings/components/users-and-teams/extra-actions';
import {
  leadFinderBulkOperationLimit,
  getUserTrackingId,
  getUserShAccountId,
  getUserPlanName,
  getUserRole,
  getUserLeadFinderPlanName,
  getIsDemoAccount,
} from '../../shared/utils/user-details';
import retryLazy from '../retry-lazy';
import V2LeadHeader from './components/header/v2-lead-header';
import V2LeadSidebar from './components/sidebar/v2-lead-sidebar';
import {
  setSidebarExpanded,
  setShowAiSearchPopup,
  setBulkPeopleSearch,
  clearBulkPeopleSearch,
  setBulkPeopleModeActive,
  openCreditNotAvailableModal,
  closeCreditNotAvailableModal,
  setSelectedLeads,
  setIsSelectedAllLeads,
  setDeSelectedLeads,
  setSelectedCompanies,
  setIsSelectedAllCompanies,
  setPeopleMaxLeadsLimit,
  setCompanyMaxLeadsLimit,
  setShowBulkRevealTagModal,
  setShowRemoveFromListModal,
  setShowGlobalRestrictionModal,
  setShowSelectedMoreThanLimitModal,
  openAddToSequenceModal,
  closeAddToSequenceModal,
  closeAiChatPanel,
  openAiChatPanel,
  setActiveChatConversationId,
  clearAiChatPendingQuery,
  resetAiChatInitialView,
} from './v2-lead-slice';
import {
  leadAddToProspectAPI,
  leadAddToSequenceAPI,
  LeadRevealFeedbackSentiment,
} from './helpers/leads.api';
import { getPostLoadMetaRequest } from '../home/extra-actions';
import { enableBulkActionRequestPolling } from '../home/home-slice';
import { LEAD_LISTING_MESSAGES } from './helpers/leads-message';
import { addCompanyMappings } from './utils/company-id-name-map';
import NoResultGradientImage from '../../shared/components/images/no-result-gradient';
import { getTagsFilterApplied } from '../prospect/helpers/helper';
import { useV2LeadBulkActions } from './components/hooks/useV2LeadBulkActions';
import { useV2LeadReveal } from './components/hooks/useV2LeadReveal';
import { useCompaniesBulkActions } from './components/hooks/useCompaniesBulkActions';
import { useV2CompanyReveal } from './components/hooks/useV2CompanyReveal';
import { useBulkReveal } from './components/hooks/useBulkReveal';
import { useLeadFinderEffects } from './components/hooks/useLeadFinderEffects';
import { useListManagement } from './components/hooks/useListManagement';
import { useRowActions } from './components/hooks/useRowActions';
import { useTabNavigation } from './components/hooks/useTabNavigation';
import { AnalyticsEvents } from '../../shared/enums/analytics';
import { Routes } from '../../shared/routes';
import hasPermission from '../../shared/utils/access-control/has-permission';
import { Permissions } from '../../shared/utils/access-control/enums/permissions';
import { useProspectDetailsFromUrl } from '../../shared/hooks/use-prospect-details-from-url';
import { PageHeaderAlertTypes } from '../../shared/utils/page-header-alert-types';
import CsvEnrichmentContainer from '../csv-enrichment/csv-enrichment-container';

const V2LeadTable = React.lazy(() =>
  retryLazy(
    () =>
      import(
        /* webpackChunkName: "v2-lead-table" */ './components/table/v2-lead-table'
      ),
  ),
);
const V2CompanyTable = React.lazy(() =>
  retryLazy(
    () =>
      import(
        /* webpackChunkName: "v2-company-table" */ './components/table/v2-company-table'
      ),
  ),
);
const AiSearchPopup = React.lazy(() =>
  retryLazy(
    () =>
      import(
        /* webpackChunkName: "v2-lead-ai-search" */ './components/ai-search-popup/ai-search-popup'
      ),
  ),
);
const AddToSequenceModal = React.lazy(() =>
  retryLazy(
    () =>
      import(
        /* webpackChunkName: "v2-lead-add-to-sequence" */ '../prospect/components/prospect-list/components/modals/add-to-sequence-modal'
      ),
  ),
);
const ProspectDetails = React.lazy(() =>
  retryLazy(
    () =>
      import(
        /* webpackChunkName: "v2-lead-prospect-details" */ '../prospect/components/prospect-list/components/prospect-details'
      ),
  ),
);
const BulkRevealTagModal = React.lazy(() =>
  retryLazy(
    () =>
      import(
        /* webpackChunkName: "v2-lead-modals" */ './components/modal/bulk-reveal-tag-modal'
      ),
  ),
);
const CreditNotAvailableModal = React.lazy(() =>
  retryLazy(
    () =>
      import(
        /* webpackChunkName: "v2-lead-modals" */ './components/modal/credit-not-available-modal'
      ),
  ),
);
const SelectedMoreThanLimitModal = React.lazy(() =>
  retryLazy(
    () =>
      import(
        /* webpackChunkName: "v2-lead-modals" */ './components/modal/selected-more-than-limit-modal'
      ),
  ),
);
const WhatsNewModal = React.lazy(() =>
  retryLazy(
    () =>
      import(
        /* webpackChunkName: "v2-lead-whats-new" */ './components/whats-new-modal'
      ),
  ),
);
const GlobalRestrictionModal = React.lazy(() =>
  retryLazy(
    () =>
      import(
        /* webpackChunkName: "v2-lead-global-restriction" */ '../home/components/global-restriction-modal'
      ),
  ),
);

const AiChatPanel = React.lazy(() =>
  retryLazy(
    () =>
      import(
        /* webpackChunkName: "v2-lead-ai-chat-panel" */ './components/ai-chat-panel/ai-chat-panel'
      ),
  ),
);

const isPrimaryTab = (value?: string): value is LeadFinderPrimaryTab =>
  !!value &&
  Object.values(LeadFinderPrimaryTab).includes(value as LeadFinderPrimaryTab);

const noResultsDescription = (
  <div>
    Results matching this query could not be displayed. Please try refining your
    search <br /> or clearing some of your filters.
  </div>
);

const noSavedLeadsDescription = (
  <div>
    You haven't saved any leads yet. Search for leads and save them to see them
    here.
  </div>
);

const V2Lead: React.FC = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const history = useHistory();
  const { tab, subtab } = useParams<{ tab?: string; subtab?: string }>();
  const {
    isSidebarExpanded,
    activeLeadType,
    activeSubTab,
    showAiSearchPopup,
    bulkPeopleSearch,
    selection,
    modals,
    aiChat,
  } = useSelector((state: RootState) => state.leadFinderV2);

  const {
    showPanel: showAiChatPanel,
    pendingQuery: aiChatPendingQuery,
    initialView: aiChatInitialView,
  } = aiChat;

  const {
    selectedLeads,
    isSelectedAllLeads,
    deSelectedLeads,
    selectedCompanies,
    isSelectedAllCompanies,
  } = selection;

  const {
    showBulkRevealTagModal,
    showRemoveFromListModal,
    showGlobalRestrictionModal,
    showSelectedMoreThanLimitModal,
    showCreditNotAvailableModal,
    creditCheckRevealType,
    showAddToSequenceModal,
    selectedLeadIdForSequence,
  } = modals;
  const loginUserEmail = useSelector((state: RootState) => state?.home?.email);
  const hasV2LeadFinderAccess = useSelector(
    (state: RootState) => state?.home?.hasV2LeadFinderAccess,
  );
  const leadFinder = useSelector((state: RootState) => state?.home?.leadFinder);
  const defaultEmailRevealType = useSelector(
    (state: RootState) => state?.home?.defaultEmailRevealType,
  );
  const getUserSettingsRequestStatus = useSelector(
    (state: RootState) => state?.home?.getUserSettingsRequest?.status,
  );
  const pageHeaderBannerAlert = useSelector(
    (state: RootState) => state?.pageHeader?.bannerAlert,
  );
  const {
    singleProspect,
    singleProspectSequenceDetails,
    getSingleProspectDetailsRequest,
    getSingleProspectSequencesDetailsRequest,
    prospectsFilters,
  } = useSelector((state: RootState) => state.prospect);

  const leadReveal = useSelector(
    (state: RootState) =>
      state?.home?.getPostLoadMetaResponse?.accountUsageQuotaRemaining?.[
        'LEAD.REVEAL'
      ],
  );
  const leadRevealBucket1 = useSelector(
    (state: RootState) =>
      state?.home?.getPostLoadMetaResponse?.accountUsageQuotaRemaining?.[
        'LEAD.REVEAL.BUCKET1'
      ],
  );
  const leadRevealBucket2 = useSelector(
    (state: RootState) =>
      state?.home?.getPostLoadMetaResponse?.accountUsageQuotaRemaining?.[
        'LEAD.REVEAL.BUCKET2'
      ],
  );
  const isBucket1Frozen = useSelector(
    (state: RootState) => state?.home?.leadFinder?.isBucket1Frozen,
  );

  const leadCredits1 = computeLeadRevealCredits(
    leadReveal,
    leadRevealBucket1,
    leadRevealBucket2,
    { isBucket1Frozen },
  );

  const leadCredits = leadCredits1.availableCredits;
  const teamsList = useSelector(
    (state: RootState) => state.usersAndTeams.teams,
  );
  const getTeamsListRequestStatus = useSelector(
    (state: RootState) => state.usersAndTeams.getTeamsRequest.status,
  );
  const isDemoAccount = useSelector(
    (state: RootState) => state.home.isDemoAccount,
  );
  const [showPhoneNumberNudge, setShowPhoneNumberNudge] = useState(false);
  const [, setIgnorePhoneNumberNudge] = useState(false);
  const keywordInputRef = useRef<HTMLInputElement>(null);
  const refreshLeadsAndStartPollingRef = useRef<() => void>(() => {});
  const maybeEnableNotificationPollingRef = useRef<(apiResponse: any) => void>(
    () => {},
  );
  const addFirebaseNotificationRef = useRef<
    (notificationUuId: string, revealType: any, totalLeads: number) => void
  >(() => {});
  // When true (bulk search mode), tab change should not retain filters in URL
  const skipUrlPushForBulkSearchRef = useRef(false);
  // Per-tab bulk-search mode (so Company bulk mode isn't lost when switching to People)
  const tabSpecificBulkModeRef = useRef<{ people: boolean; company: boolean }>({
    people: false,
    company: false,
  });
  const companyFilterRestoreRef = useRef<
    ((params: URLSearchParams) => void) | null
  >(null);
  const peopleFilterRestoreRef = useRef<
    ((params: URLSearchParams) => void) | null
  >(null);

  const [emailRevealingLeads, setEmailRevealingLeads] = useState<
    Record<number, boolean>
  >({});
  const [emailPhoneRevealingLeads, setEmailPhoneRevealingLeads] = useState<
    Record<number, boolean>
  >({});
  const [
    personalEmailRevealingLeads,
    setPersonalEmailRevealingLeads,
  ] = useState<Record<number, boolean>>({});
  const [enrichingCompanies, setEnrichingCompanies] = useState<
    Record<number, boolean>
  >({});
  const [loadingAddToProspect, setLoadingAddToProspect] = useState<
    Record<number, boolean>
  >({});
  const [loadingAddToSequence, setLoadingAddToSequence] = useState<
    Record<number, boolean>
  >({});
  // Use the reusable hook to handle URL-based prospect details modal
  const {
    selectedProspectId: selectedContactId,
    showProspectDetailsModal,
    defaultTab,
    hideShowProspectDetailsModal: hideShowProspectDetailsModalFromHook,
    openProspectDetailsModal,
  } = useProspectDetailsFromUrl({
    // Dynamic pattern: matches /v2/leads/:anyTab/:anySubtab/:prospectId
    // Excludes csv-enrichment paths (they use MongoDB ObjectIds, not prospect IDs)
    pathnamePattern: /\/v2\/leads\/(?!csv-enrichment)[^/]+\/[^/]+\/(\d+)/,
  });
  const [isBulkFindPeopleLoading, setIsBulkFindPeopleLoading] = useState(false);
  const [showWhatsNewModal, setShowWhatsNewModal] = useState(false);

  const leadFinderBulkOperationLimitValue = leadFinderBulkOperationLimit();

  // Check if user has access (wait for API to complete)
  const shouldBlockAccess =
    getIsRequestSucceeded(getUserSettingsRequestStatus) &&
    !hasV2LeadFinderAccess;

  const {
    savedFilters,
    setSavedFilters,
    handleDefaultValues,
    handleQuery,
  } = useSavedLeadFilter();
  const { leadTags, isLeadTagsLoading, fetchLeadTags } = useLeadApiState();

  const isCsvEnrichmentRoute = location.pathname.startsWith(
    '/v2/leads/csv-enrichment',
  );
  const currentTabFromUrl = isPrimaryTab(tab)
    ? tab
    : LeadFinderPrimaryTab.PEOPLE;
  const isPeopleTabFromUrl = currentTabFromUrl === LeadFinderPrimaryTab.PEOPLE;
  const shouldBlockPeopleFinder =
    shouldBlockAccess || !isPeopleTabFromUrl || isCsvEnrichmentRoute;

  const {
    isLoading,
    isLoadingCounts,
    isLoadingSavedCounts,
    leadsData,
    handleRecentSearch,
    handleGeneratePayload,
    setLeadsData,
    handleRouteChange,
    searchLeads,
    handleFetchCounts,
    handlePagination,
    handleAppendLead,
    countsData,
    countsError,
    apiParam,
    savedApiParam,
    peopleListsData,
    companyListsData: peopleTabCompanyListsData,
    isLoadingPeopleLists,
    refreshPeopleLists,
    refreshCompanyLists: refreshCompanyListsForPeopleTab,
    skipNextCountCallRef,
    handleFetchSavedCounts,
  } = useLeadFinder(
    activeSubTab,
    setSavedFilters as React.Dispatch<React.SetStateAction<SavedTabFilters>>,
    setEmailRevealingLeads,
    setEmailPhoneRevealingLeads,
    shouldBlockPeopleFinder,
    activeLeadType,
    skipUrlPushForBulkSearchRef,
  );

  const refreshPeopleSavedCounts = useCallback(async () => {
    const countPayload: Record<string, any> = {};
    if (savedFilters.tags?.length > 0) {
      countPayload.tags = savedFilters.tags;
    }
    if (savedFilters.search) {
      countPayload.search = savedFilters.search;
    }
    if (savedFilters.createdDate?.length === 2) {
      const [startDate, endDate] = savedFilters.createdDate;
      countPayload.createdDate = `${dateToString(startDate)},${dateToString(
        endDate,
      )}`;
    }
    if (
      savedFilters.listIds?.includes?.length > 0 ||
      savedFilters.listIds?.excludes?.length > 0
    ) {
      countPayload.listIds = savedFilters.listIds;
    }
    if (savedFilters?.excludeProspects) {
      countPayload.excludeProspects = savedFilters.excludeProspects;
    }
    if (savedFilters?.emailStatus?.length > 0) {
      countPayload.emailStatus = savedFilters.emailStatus;
    }
    await handleFetchSavedCounts(countPayload);
  }, [handleFetchSavedCounts, savedFilters]);

  // Company finder hook - only active when Company tab is selected
  const currentTabFromUrlForCompany = isPrimaryTab(tab)
    ? tab
    : LeadFinderPrimaryTab.PEOPLE;
  const isCompanyTabFromUrl =
    currentTabFromUrlForCompany === LeadFinderPrimaryTab.COMPANY;
  const isCompanyTab = activeLeadType === LeadFinderPrimaryTab.COMPANY;
  const isCsvEnrichmentTab =
    activeLeadType === LeadFinderPrimaryTab.CSV_ENRICHMENT;
  const {
    isLoading: isCompanyLoading,
    isLoadingCounts: isLoadingCompanyCounts,
    isLoadingSavedCounts: isLoadingCompanySavedCounts,
    companiesData,
    handleGeneratePayload: handleCompanyGeneratePayload,
    handleRecentSearch: handleCompanyRecentSearch,
    searchCompanies,
    handleFetchCounts: handleCompanyFetchCounts,
    handleFetchSavedCounts: handleCompanyFetchSavedCounts,
    handlePagination: handleCompanyPagination,
    countsData: companyCountsData,
    countsError: companyCountsError,
    companyListsData,
    isLoadingLists: isLoadingCompanyLists,
    refreshCompanyLists,
    refreshSavedCounts,
    savedApiParam: companySavedApiParam,
    apiParam: companyApiParam,
    setCompaniesData,
    getCompanyNames,
    signalsListData,
    newsListData,
  } = useCompanyFinder(
    activeSubTab,
    shouldBlockAccess || !isCompanyTabFromUrl || isCsvEnrichmentRoute, // Block when not on company tab (check URL, not Redux)
    activeLeadType,
    skipUrlPushForBulkSearchRef,
  );

  // --- useTabNavigation ---
  const {
    preservedFiltersRef,
    tabSpecificFiltersRef,
    handleLeadTypeChange,
    handleSubTabChange,
    handleClearAllPeople,
    handleClearAllCompany,
    getAllFiltersExceptPagination,
  } = useTabNavigation({
    setLeadsData,
    setCompaniesData,
    handleFetchCounts,
    handleCompanyFetchCounts,
    searchLeads,
    searchCompanies,
    handleRouteChange,
    setSavedFilters,
    handleDefaultValues,
    refreshPeopleLists,
    refreshCompanyLists,
    skipNextCountCallRef,
    skipUrlPushForBulkSearchRefProp: skipUrlPushForBulkSearchRef,
    tabSpecificBulkModeRef,
  });

  const syncCompanyTabSpecificFiltersRef = useCallback(
    (filters: URLSearchParams) => {
      const normalized = getAllFiltersExceptPagination(
        new URLSearchParams(filters.toString()),
      );
      tabSpecificFiltersRef.current.company = normalized.toString();
    },
    [getAllFiltersExceptPagination, tabSpecificFiltersRef],
  );

  const syncPeopleTabSpecificFiltersRef = useCallback(
    (filters: URLSearchParams) => {
      const normalized = getAllFiltersExceptPagination(
        new URLSearchParams(filters.toString()),
      );
      tabSpecificFiltersRef.current.people = normalized.toString();
    },
    [getAllFiltersExceptPagination, tabSpecificFiltersRef],
  );

  useEffect(() => {
    if (!location.pathname.includes('/v2/leads/company/')) return;
    if (activeLeadType !== LeadFinderPrimaryTab.COMPANY) return;

    const currentParams = new URLSearchParams(location.search);
    const nonPagination = getAllFiltersExceptPagination(
      currentParams,
    ).toString();

    if (nonPagination) {
      tabSpecificFiltersRef.current.company = nonPagination;
      return;
    }

    const hasPreservedCompanyFilters =
      (tabSpecificFiltersRef.current.company || '').trim().length > 0;
    if (
      !tabSpecificBulkModeRef.current.company &&
      activeSubTab !== LeadFinderSecondaryTab.SAVED &&
      !hasPreservedCompanyFilters
    ) {
      tabSpecificFiltersRef.current.company = '';
    }
  }, [
    activeLeadType,
    activeSubTab,
    location.pathname,
    location.search,
    getAllFiltersExceptPagination,
  ]);

  // --- useLeadFinderEffects ---
  useLeadFinderEffects({
    leadFinder,
    setShowWhatsNewModal,
    shouldBlockAccess,
    preservedFiltersRef,
    tabSpecificFiltersRef,
    skipUrlPushForBulkSearchRef,
    tabSpecificBulkModeRef,
    bulkPeopleSearch,
    fetchLeadTags,
    keywordInputRef,
    tab,
    subtab,
  });

  // --- useRowActions ---
  const {
    addToListOpenFromRow,
    setAddToListOpenFromRow,
    companyIdToAddFromRow,
    setCompanyIdToAddFromRow,
    leadIdToAddFromRow,
    setLeadIdToAddFromRow,
    triggerOpenAddTag,
    setTriggerOpenAddTag,
    triggerOpenRemoveTag,
    setTriggerOpenRemoveTag,
    companyIdToAddFromRowRef,
    leadIdToAddFromRowRef,
    handleLeadRowRemoveFromList,
    handleLeadRowAddTag,
    handleLeadRowRemoveTag,
    handleRowTagModalClosed,
    handleAddToListOpenChange,
    handleSingleLeadAddToList,
    overrideSelectedLeadObjects,
    overrideSelectedCompanyObjects,
  } = useRowActions({
    leadsData,
    companiesData,
    leadTags,
    leadCredits,
    leadFinderBulkOperationLimitValue,
    defaultEmailRevealType,
    activeSubTab,
  });

  const handleCompanyTriggerSearchWithFilters = useCallback(
    (filters: URLSearchParams) => {
      const payload = handleCompanyGeneratePayload(filters);
      const normalized = new URLSearchParams(filters.toString());
      ['page', 'start', 'take'].forEach((key) => normalized.delete(key));
      tabSpecificFiltersRef.current.company = normalized.toString();
      if (activeSubTab !== LeadFinderSecondaryTab.SAVED) {
        handleCompanyFetchCounts(payload);
      }
      searchCompanies(payload);
      tabSpecificFiltersRef.current.company = filters.toString();
    },
    [
      dispatch,
      handleCompanyGeneratePayload,
      searchCompanies,
      handleCompanyFetchCounts,
      activeSubTab,
      tabSpecificFiltersRef,
    ],
  );

  const handlePeopleTriggerSearchWithFilters = useCallback(
    (filters: URLSearchParams) => {
      const payload = handleGeneratePayload(filters);
      if (activeSubTab !== LeadFinderSecondaryTab.SAVED) {
        handleFetchCounts(payload);
      }
      searchLeads(payload);
      tabSpecificFiltersRef.current.people = filters.toString();
    },
    [
      dispatch,
      handleGeneratePayload,
      searchLeads,
      handleFetchCounts,
      handleFetchSavedCounts,
      activeSubTab,
      tabSpecificFiltersRef,
    ],
  );

  const handleBulkFindPeople = useCallback(async () => {
    try {
      setIsBulkFindPeopleLoading(true);
      let companyIds: string[] = [];
      const wasCompanyBulkMode =
        tabSpecificBulkModeRef.current.company ||
        skipUrlPushForBulkSearchRef.current;

      if (isSelectedAllCompanies) {
        const companyFiltersSource = tabSpecificFiltersRef.current.company
          ? new URLSearchParams(tabSpecificFiltersRef.current.company)
          : undefined;
        const companyNamesData = await getCompanyNames(companyFiltersSource);

        if (!Array.isArray(companyNamesData) || companyNamesData.length === 0) {
          toaster.error('No companies found to search for people.');
          setIsBulkFindPeopleLoading(false);
          return;
        }

        companyIds = Array.from(
          new Set(
            companyNamesData
              .map((item) => item.companyId?.toString())
              .filter(Boolean),
          ),
        );

        const nameMappings: Record<string, string> = {};
        companyNamesData.forEach((item) => {
          if (item.companyId && item.companyName) {
            nameMappings[String(item.companyId)] = item.companyName;
          }
        });
        addCompanyMappings(loginUserEmail || '', nameMappings);
      } else {
        if (!companiesData?.profiles) {
          toaster.error('No companies found to search for people.');
          setIsBulkFindPeopleLoading(false);
          return;
        }

        companyIds = Array.from(
          new Set(
            selectedCompanies
              ?.map((companyId) => {
                const id = companyId;
                return id ? String(id) : null;
              })
              .filter(Boolean) as string[],
          ),
        );

        const nameMappings: Record<string, string> = {};
        (companiesData?.profiles || []).forEach((company: any) => {
          const id = company?.id ?? company?.company_id;
          const name = company?.company_name || company?.name;
          if (id != null && name) {
            nameMappings[String(id)] = name;
          }
        });
        addCompanyMappings(loginUserEmail || '', nameMappings);
      }

      const currentParams = new URLSearchParams(location.search);
      const companyFiltersFromUrl = getAllFiltersExceptPagination(
        currentParams,
      );
      const hasCompanyFiltersInUrl =
        companyFiltersFromUrl.toString().length > 0;
      const effectiveCompanyFilters = hasCompanyFiltersInUrl
        ? companyFiltersFromUrl.toString()
        : tabSpecificFiltersRef.current.company || '';
      tabSpecificFiltersRef.current.company = effectiveCompanyFilters;
      tabSpecificFiltersRef.current.people = '';

      const searchParams = new URLSearchParams();

      if (isSelectedAllCompanies) {
        const bulkFilters = new URLSearchParams();
        if (companyIds.length > 0) {
          companyIds.forEach((id) => bulkFilters.append('company_ids', id));
        }
        dispatch(setBulkPeopleSearch({ filters: bulkFilters.toString() }));
        dispatch(setBulkPeopleModeActive(true));
        skipUrlPushForBulkSearchRef.current = true;
        if (skipNextCountCallRef) {
          skipNextCountCallRef.current = true;
        }
      } else {
        dispatch(setBulkPeopleModeActive(false));
        dispatch(clearBulkPeopleSearch());
        if (companyIds.length > 0) {
          companyIds.forEach((id) => searchParams.append('company_ids', id));
        }
        tabSpecificFiltersRef.current.people = searchParams.toString();
        await delay();
      }

      history.push(
        `/v2/leads/${LeadFinderPrimaryTab.PEOPLE}/${
          LeadFinderSecondaryTab.TOTAL
        }?${searchParams.toString()}`,
        {
          preservedCompanyFilters: effectiveCompanyFilters,
        } as any,
      );
      tabSpecificBulkModeRef.current.company = wasCompanyBulkMode;
      dispatch(setSelectedCompanies([]));
      dispatch(setIsSelectedAllCompanies(false));
    } catch (error: any) {
      setIsBulkFindPeopleLoading(false);
      if (!error?.isHandled) {
        toaster.error(
          error?.message || 'Failed to get company names. Please try again.',
        );
      }
    }
  }, [
    getCompanyNames,
    history,
    location.search,
    selectedCompanies,
    isSelectedAllCompanies,
    companiesData,
    loginUserEmail,
    tabSpecificFiltersRef,
    tabSpecificBulkModeRef,
    skipUrlPushForBulkSearchRef,
  ]);

  const wasBulkPendingRef = useRef(false);
  useEffect(() => {
    if (bulkPeopleSearch.isPending && !wasBulkPendingRef.current) {
      tabSpecificFiltersRef.current.people = bulkPeopleSearch.filters;
    }
    if (wasBulkPendingRef.current && !bulkPeopleSearch.isPending) {
      setIsBulkFindPeopleLoading(false);
    }
    wasBulkPendingRef.current = bulkPeopleSearch.isPending;
  }, [bulkPeopleSearch.isPending, bulkPeopleSearch.filters]);

  useEffect(() => {
    if (activeLeadType !== LeadFinderPrimaryTab.COMPANY) {
      setIsBulkFindPeopleLoading(false);
    }
  }, [activeLeadType]);

  const isFirstSavedFiltersRender = useRef(true);
  useEffect(() => {
    if (isFirstSavedFiltersRender.current) {
      isFirstSavedFiltersRender.current = false;
      return;
    }
    dispatch(setSelectedLeads([]));
    dispatch(setIsSelectedAllLeads(false));
    dispatch(setDeSelectedLeads([]));
    dispatch(setSelectedCompanies([]));
    dispatch(setIsSelectedAllCompanies(false));
  }, [savedFilters]);

  const userUrlsPeople = useMemo(() => {
    const stored = StorageUtil.getItem(
      V2_LEAD_FINDER_RECENT_SEARCHES_STORAGE_KEY,
    );
    if (!stored) return [];
    try {
      return JSON.parse(stored)?.[loginUserEmail] || [];
    } catch (_error) {
      return [];
    }
  }, [loginUserEmail]);

  const userUrlsCompany = useMemo(() => {
    const stored = StorageUtil.getItem(
      V2_LEAD_FINDER_RECENT_SEARCHES_STORAGE_KEY_COMPANY,
    );
    if (!stored) return [];
    try {
      return JSON.parse(stored)?.[loginUserEmail] || [];
    } catch (_error) {
      return [];
    }
  }, [loginUserEmail]);

  const [storedUrlsPeople, setStoredUrlsPeople] = useState<string[]>(
    userUrlsPeople || [],
  );
  const [storedUrlsCompany, setStoredUrlsCompany] = useState<string[]>(
    userUrlsCompany || [],
  );

  // Keep state in sync when login user changes
  useEffect(() => {
    setStoredUrlsPeople(userUrlsPeople || []);
  }, [userUrlsPeople]);
  useEffect(() => {
    setStoredUrlsCompany(userUrlsCompany || []);
  }, [userUrlsCompany]);

  const refreshStoredUrlsPeople = useCallback(() => {
    const stored = StorageUtil.getItem(
      V2_LEAD_FINDER_RECENT_SEARCHES_STORAGE_KEY,
    );
    if (!stored) {
      setStoredUrlsPeople([]);
      return;
    }
    try {
      const parsed = JSON.parse(stored);
      setStoredUrlsPeople(parsed?.[loginUserEmail] || []);
    } catch (_error) {
      setStoredUrlsPeople([]);
    }
  }, [loginUserEmail]);

  const refreshStoredUrlsCompany = useCallback(() => {
    const stored = StorageUtil.getItem(
      V2_LEAD_FINDER_RECENT_SEARCHES_STORAGE_KEY_COMPANY,
    );
    if (!stored) {
      setStoredUrlsCompany([]);
      return;
    }
    try {
      const parsed = JSON.parse(stored);
      setStoredUrlsCompany(parsed?.[loginUserEmail] || []);
    } catch (_error) {
      setStoredUrlsCompany([]);
    }
  }, [loginUserEmail]);

  // Refresh storedUrls when handleRecentSearch is called
  // We'll wrap handleRecentSearch to also refresh the URLs
  const handleRecentSearchWithRefresh = useCallback(
    (source?: URLSearchParams) => {
      handleRecentSearch(source);
      // Refresh storedUrls after a short delay to ensure localStorage is updated
      setTimeout(() => {
        refreshStoredUrlsPeople();
      }, 100);
    },
    [handleRecentSearch, refreshStoredUrlsPeople],
  );

  const handleCompanyRecentSearchWithRefresh = useCallback(
    (source?: URLSearchParams) => {
      handleCompanyRecentSearch?.(source);
      setTimeout(() => {
        refreshStoredUrlsCompany();
      }, 100);
    },
    [handleCompanyRecentSearch, refreshStoredUrlsCompany],
  );

  const onRestoreCompanyFilterUI = useCallback((params: URLSearchParams) => {
    companyFilterRestoreRef.current?.(params);
  }, []);

  const onRestorePeopleFilterUI = useCallback((params: URLSearchParams) => {
    peopleFilterRestoreRef.current?.(params);
  }, []);

  const handleSelectAllAcrossPages = useCallback(
    (isSelected: boolean) => {
      if (activeLeadType === LeadFinderPrimaryTab.PEOPLE) {
        if (!isSelected) {
          dispatch(setIsSelectedAllLeads(false));
          dispatch(setSelectedLeads([]));
          dispatch(setDeSelectedLeads([]));
          dispatch(setPeopleMaxLeadsLimit(null));
          return;
        }
        const totalPeople = leadsData?.pagination?.totalRows || 0;
        const bulkCap = getV2BulkOperationLimit(activeSubTab, {
          isCompanyTab: false,
        });
        // Saved tab: leads are already revealed — skip credit-based cap
        const isSavedSubTab = activeSubTab === LeadFinderSecondaryTab.SAVED;
        let maxSelectable: number;
        if (isSavedSubTab) {
          maxSelectable = Math.min(totalPeople, bulkCap);
        } else {
          // Credit-based cap for "Select all leads" on Total / New tabs
          const costPerLead =
            defaultEmailRevealType === 'personal_email' ? 2.0 : 1.0;
          const rawMax = Math.floor(Number(leadCredits) / costPerLead);
          maxSelectable = Math.min(rawMax, totalPeople, bulkCap);
          if (maxSelectable <= 0) {
            dispatch(openCreditNotAvailableModal(LeadRevealType.Email));
            return;
          }
        }
        const pageIds = (leadsData?.profiles || []).map((l) => l.id);
        dispatch(setSelectedLeads(pageIds));
        dispatch(setIsSelectedAllLeads(true));
        dispatch(setPeopleMaxLeadsLimit(maxSelectable));
      } else if (activeLeadType === LeadFinderPrimaryTab.COMPANY) {
        if (!isSelected) {
          dispatch(setIsSelectedAllCompanies(false));
          dispatch(setSelectedCompanies([]));
          dispatch(setCompanyMaxLeadsLimit(null));
          return;
        }
        // Company selection uses bulk-operation limit only — NOT credit-gated.
        // Credits are only consumed when companies are revealed, not when selected.
        // const COMPANY_CREDIT_COST = 0.1;
        const totalCompanies = companiesData?.pagination?.totalRows || 0;
        // const rawMax = Math.floor(Number(leadCredits) / COMPANY_CREDIT_COST);
        const bulkCap = getV2BulkOperationLimit(activeSubTab, {
          isCompanyTab: true,
        });
        const maxSelectable = Math.min(totalCompanies, bulkCap);
        // if (maxSelectable <= 0) {
        //   dispatch(openCreditNotAvailableModal(LeadRevealType.Company));
        //   return;
        // }
        const currentPageCompanyIds = (
          companiesData?.profiles || []
        ).map((company) => Number(company.id || company.company_id || 0));
        dispatch(setSelectedCompanies(currentPageCompanyIds));
        dispatch(setIsSelectedAllCompanies(true));
        dispatch(setCompanyMaxLeadsLimit(maxSelectable));
      }
    },
    [
      activeLeadType,
      activeSubTab,
      dispatch,
      leadCredits,
      defaultEmailRevealType,
      leadsData?.profiles,
      leadsData?.pagination?.totalRows,
      companiesData?.profiles,
      companiesData?.pagination?.totalRows,
    ],
  );

  // Bulk actions hook
  const {
    loadingBulkAction: leadLoadingBulkAction,
    handleBulkAddToSequence,
    handleBulkAction: handleBulkActionFromHook,
    tagButtonProps,
  } = useV2LeadBulkActions({
    selectedLeads,
    setSelectedLeads: (val) => dispatch(setSelectedLeads(val as number[])),
    isSelectedAllLeads,
    setIsSelectedAllLeads: (val) =>
      dispatch(setIsSelectedAllLeads(val as boolean)),
    deSelectedLeads,
    setDeSelectedLeads: (val) => dispatch(setDeSelectedLeads(val as number[])),
    activeSubTab,
    savedCount: countsData?.revealedCount,
    handleGeneratePayload,
    searchLeads,
    setShowAddToSequenceModal: (val) =>
      dispatch(val ? openAddToSequenceModal(null) : closeAddToSequenceModal()),
    leadTags,
    savedFilters,
    setSavedFilters,
    handleQuery,
    leadsData,
    fetchLeadTags,
  });

  const {
    loadingBulkAction: companyLoadingBulkAction,
    handleCompanyExport,
    companyTagButtonProps,
  } = useCompaniesBulkActions({
    activeLeadType,
    activeSubTab,
    savedCount: companyCountsData?.savedCount,
    selectedCompanies,
    isSelectedAllCompanies,
    savedFilters,
    searchCompanies,
    fetchLeadTags,
    refreshSavedCounts,
    companiesData,
    leadTags,
    setSavedFilters,
    handleQuery,
    setSelectedCompanies: (val) =>
      dispatch(setSelectedCompanies(val as number[])),
    setIsSelectedAllCompanies: (val) =>
      dispatch(setIsSelectedAllCompanies(val as boolean)),
  });

  const loadingBulkAction =
    activeLeadType === LeadFinderPrimaryTab.PEOPLE
      ? leadLoadingBulkAction
      : {
          loadingAddToProspect: false,
          loadingAddToSequence: false,
          loadingExport: companyLoadingBulkAction.loadingExport,
        };

  const handlePeopleGeneratePayloadWithBulkFallback = useCallback(
    (source?: string | URLSearchParams) => {
      if (source === undefined) {
        if (activeSubTab === LeadFinderSecondaryTab.SAVED) {
          return handleGeneratePayload(source);
        }

        const urlParams = new URLSearchParams(window.location.search);
        urlParams.delete('page');
        urlParams.delete('start');
        urlParams.delete('take');
        const urlHasFilters = urlParams.toString().length > 0;
        if (!urlHasFilters && tabSpecificFiltersRef.current.people) {
          return handleGeneratePayload(tabSpecificFiltersRef.current.people);
        }
      }
      return handleGeneratePayload(source);
    },
    [activeSubTab, handleGeneratePayload, tabSpecificFiltersRef],
  );

  const handleCompanyGeneratePayloadWithBulkFallback = useCallback(
    (source?: string | URLSearchParams) => {
      if (source === undefined) {
        if (activeSubTab === LeadFinderSecondaryTab.SAVED) {
          return handleCompanyGeneratePayload(source);
        }

        const urlParams = new URLSearchParams(window.location.search);
        urlParams.delete('page');
        urlParams.delete('start');
        urlParams.delete('take');
        const urlHasFilters = urlParams.toString().length > 0;
        if (!urlHasFilters && tabSpecificFiltersRef.current.company) {
          return handleCompanyGeneratePayload(
            tabSpecificFiltersRef.current.company,
          );
        }
      }
      return handleCompanyGeneratePayload(source);
    },
    [activeSubTab, handleCompanyGeneratePayload, tabSpecificFiltersRef],
  );

  // Lead reveal hook
  const {
    handleRevealLead,
    handleBulkReveal,
    refreshLeadsAndStartPolling,
    addFirebaseNotification,
    selectAllRevealType,
  } = useV2LeadReveal({
    selectedLeads,
    isSelectedAllLeads,
    deSelectedLeads,
    leadsData,
    handleGeneratePayload: handlePeopleGeneratePayloadWithBulkFallback,
    setLeadsData,
    setEmailRevealingLeads,
    setEmailPhoneRevealingLeads,
    setPersonalEmailRevealingLeads,
    personalEmailRevealingLeads,
    emailRevealingLeads,
    emailPhoneRevealingLeads,
    setIsSelectedAllLeads: (val) =>
      dispatch(setIsSelectedAllLeads(val as boolean)),
    setSelectedLeads: (val) => dispatch(setSelectedLeads(val as number[])),
    setDeSelectedLeads: (val) => dispatch(setDeSelectedLeads(val as number[])),
    activeSubTab,
    apiParam,
    savedApiParam,
    isLoading,
  });

  const maybeEnableNotificationPolling = useCallback(
    (apiResponse: any) => {
      if (apiResponse?.payload?.shouldPoll) {
        dispatch(enableBulkActionRequestPolling());
      }
    },
    [dispatch],
  );

  refreshLeadsAndStartPollingRef.current = refreshLeadsAndStartPolling;
  maybeEnableNotificationPollingRef.current = maybeEnableNotificationPolling;
  addFirebaseNotificationRef.current = addFirebaseNotification;

  // Company reveal hook
  const {
    handleRevealCompany: handleRevealCompanyFromHook,
    handleBulkRevealResponse,
  } = useV2CompanyReveal({
    companiesData,
    handleGeneratePayload: handleCompanyGeneratePayloadWithBulkFallback,
    setCompaniesData,
    setEnrichingCompanies,
    enrichingCompanies,
    activeSubTab,
    apiParam: companyApiParam,
    savedApiParam: companySavedApiParam,
    isLoading: isCompanyLoading,
  });

  const handleRevealCompanyWithCreditCheck = useCallback(
    (companyId: number) => {
      if (Number(leadCredits) < 0.1) {
        dispatch(openCreditNotAvailableModal(LeadRevealType.Company));
        window.analytics?.track?.({
          userId: getUserTrackingId(),
          event: AnalyticsEvents.TrippedFeature,
          properties: {
            shAccountId: getUserShAccountId(),
            current_plan: getUserPlanName(),
            role: getUserRole(),
            'LF Plan': getUserLeadFinderPlanName(),
            feature_type: 'Lead Finder',
          },
        });
        return;
      }
      handleRevealCompanyFromHook(companyId);
    },
    [leadCredits, handleRevealCompanyFromHook, dispatch],
  );

  // --- useListManagement ---
  const {
    isCreateListLoading,
    isDeleteListLoading,
    loadingRemoveFromList,
    handleCreateListOnly,
    handleSaveList,
    handleDeleteList,
    handleExportList,
    handleAddToList,
    handleRemoveFromList,
  } = useListManagement({
    activeLeadType,
    activeSubTab,
    leadsData,
    setLeadsData,
    apiParam,
    savedApiParam,
    handleGeneratePayload: handlePeopleGeneratePayloadWithBulkFallback,
    searchLeads,
    refreshPeopleLists,
    refreshCompanyListsForPeopleTab,
    companiesData,
    setCompaniesData,
    companySavedApiParam,
    handleCompanyGeneratePayload: handleCompanyGeneratePayloadWithBulkFallback,
    searchCompanies,
    refreshCompanyLists,
    savedFilters,
    setSavedFilters,
    companyIdToAddFromRowRef,
    leadIdToAddFromRowRef,
    refreshLeadsAndStartPollingRef,
    maybeEnableNotificationPollingRef,
    addFirebaseNotificationRef,
    leadCredits,
    isDemoAccount,
    isCompanyTab,
    handleCompanyTriggerSearchWithFilters,
    handleBulkRevealResponse,
    setEnrichingCompanies,
    companyApiParam,
    handleFetchSavedCounts: isCompanyTab
      ? handleCompanyFetchSavedCounts
      : handleFetchSavedCounts,
  });

  // --- useBulkReveal ---
  const {
    isBulkRevealing,
    tagsToAdd,
    setTagsToAdd,
    setLeadRevealType,
    bulkRevealEffectiveCount,
    handleBulkRevealWithTags,
    handleBulkRevealSubmit,
  } = useBulkReveal({
    handleBulkReveal,
    leadsData,
    leadCredits,
    activeSubTab,
  });

  const hasPaymentPendingBanner =
    pageHeaderBannerAlert === PageHeaderAlertTypes.PAYMENT_PENDING;

  const hasDemoAccountBanner = getIsDemoAccount();

  const mainContentClass = classNames('v2-lead-content', {
    'v2-lead-content--sidebar-collapsed': !isSidebarExpanded,
    'v2-lead-content--with-payment-banner': hasPaymentPendingBanner,
    'v2-lead-content--with-demo-banner': hasDemoAccountBanner,
  });

  const redirectToPeopleWithCompanyIds = useCallback(
    (companyIds: string[]) => {
      skipUrlPushForBulkSearchRef.current = false;
      dispatch(setBulkPeopleModeActive(false));
      dispatch(clearBulkPeopleSearch());

      const currentParams = new URLSearchParams(location.search);
      const companyFiltersFromUrl = getAllFiltersExceptPagination(
        currentParams,
      );
      tabSpecificFiltersRef.current.company =
        companyFiltersFromUrl.toString().length > 0
          ? companyFiltersFromUrl.toString()
          : tabSpecificFiltersRef.current.company || '';
      const searchParams = new URLSearchParams();
      companyIds.forEach((id) => searchParams.append('company_ids', id));
      tabSpecificFiltersRef.current.people = searchParams.toString();
      history.push(
        `/v2/leads/${LeadFinderPrimaryTab.PEOPLE}/${
          LeadFinderSecondaryTab.TOTAL
        }?${searchParams.toString()}`,
      );
    },
    [location.search, getAllFiltersExceptPagination, history, dispatch],
  );

  // Company table callbacks (extracted from inline to stabilize references)
  // Latest-value ref so handleCompanySelect stays referentially stable across
  // selections (same rationale as selectedLeadsRef — keeps the shared
  // ColumnCell memo from re-rendering every cell on each selection).
  const selectedCompaniesRef = useRef(selectedCompanies);
  selectedCompaniesRef.current = selectedCompanies;

  const handleCompanySelect = useCallback(
    (companyId: number) => {
      const { current } = selectedCompaniesRef;
      const prevSet = new Set(current);
      if (prevSet?.has(companyId)) {
        if (isSelectedAllCompanies) {
          dispatch(setIsSelectedAllCompanies(false));
        }
        dispatch(
          setSelectedCompanies(current.filter((id) => id !== companyId)),
        );
      } else if (current.length < 10000) {
        dispatch(setSelectedCompanies([...current, companyId]));
      }
    },
    [isSelectedAllCompanies, dispatch],
  );

  const handleCompanySelectAll = useCallback(
    (companyIds: number[]) => {
      dispatch(setSelectedCompanies(companyIds));
      dispatch(setIsSelectedAllCompanies(false));
    },
    [dispatch],
  );

  const handleCompanyPaginationChange = useCallback(
    (page: number, limit: number) => {
      const urlParams = new URLSearchParams(window.location.search);
      urlParams.delete('page');
      urlParams.delete('start');
      urlParams.delete('take');
      const filterSource =
        activeSubTab !== LeadFinderSecondaryTab.SAVED &&
        !urlParams.toString() &&
        tabSpecificFiltersRef.current.company
          ? tabSpecificFiltersRef.current.company
          : undefined;
      handleCompanyPagination({ page, limit, filterSource });
    },
    [activeSubTab, handleCompanyPagination, tabSpecificFiltersRef],
  );

  const handleSaveCompanyFromRow = useCallback((companyId: number) => {
    companyIdToAddFromRowRef.current = companyId;
    setCompanyIdToAddFromRow(companyId);
    setAddToListOpenFromRow(true);
  }, []);

  const handleFindPeople = useCallback(
    (company: any) => {
      const companyId = company?.company_id;
      if (!companyId) {
        toaster.error('Company ID not found.');
        return;
      }

      const companyName = company?.company_name || company?.name;
      if (companyName) {
        addCompanyMappings(loginUserEmail || '', {
          [String(companyId)]: companyName,
        });
      }
      redirectToPeopleWithCompanyIds([String(companyId)]);
    },
    [redirectToPeopleWithCompanyIds, loginUserEmail],
  );

  const handleRowAddToList = useCallback((companyId: number) => {
    companyIdToAddFromRowRef.current = companyId;
    setCompanyIdToAddFromRow(companyId);
    setAddToListOpenFromRow(true);
  }, []);

  const handleRowRemoveFromList = useCallback(
    (companyId: number) => {
      dispatch(setSelectedCompanies([companyId]));
      dispatch(setIsSelectedAllCompanies(false));
      dispatch(setShowRemoveFromListModal(true));
    },
    [dispatch],
  );

  const handleRowAddTag = useCallback(
    (companyId: number, options?: { skipSelection?: boolean }) => {
      if (!options?.skipSelection) {
        dispatch(setSelectedCompanies([companyId]));
        dispatch(setIsSelectedAllCompanies(false));
      }
      setTriggerOpenAddTag(companyId);
    },
    [dispatch, setTriggerOpenAddTag],
  );

  const handleRowRemoveTag = useCallback(
    (companyId: number, options?: { skipSelection?: boolean }) => {
      if (!options?.skipSelection) {
        dispatch(setSelectedCompanies([companyId]));
        dispatch(setIsSelectedAllCompanies(false));
      }
      setTriggerOpenRemoveTag(companyId);
    },
    [dispatch, setTriggerOpenRemoveTag],
  );

  // `onRowSelectHandler` prop the shared ColumnCell memo compares
  const selectedLeadsRef = useRef(selectedLeads);
  selectedLeadsRef.current = selectedLeads;

  const handleLeadSelect = useCallback(
    (leadId: number) => {
      const { current } = selectedLeadsRef;
      if (current.includes(leadId)) {
        dispatch(setSelectedLeads(current.filter((id) => id !== leadId)));
      } else if (current.length < 10000) {
        dispatch(setSelectedLeads([...current, leadId]));
      }
    },
    [dispatch],
  );

  // Stable setters for the memoized V2LeadSidebar. Previously these were inline
  // arrows in the JSX, so they were recreated on every container render — which
  // broke React.memo(V2LeadSidebar) and re-rendered the entire (expensive)
  // filter on every selection. Keeping them stable lets the sidebar skip those
  // renders, so selecting a row no longer re-runs the filter.
  const handleSidebarSetSelectedLeads = useCallback(
    (val: number[]) => dispatch(setSelectedLeads(val)),
    [dispatch],
  );

  const handleSidebarSetSelectedCompanies = useCallback(
    (val: number[]) => dispatch(setSelectedCompanies(val)),
    [dispatch],
  );

  const handleLeadSelectAll = useCallback(
    (leadIds: number[]) => {
      // Replace selection with the provided IDs
      dispatch(setSelectedLeads(leadIds));
      dispatch(setIsSelectedAllLeads(false));
      dispatch(setDeSelectedLeads([]));
    },
    [dispatch],
  );

  const hideShowProspectDetailsModal = useCallback(() => {
    hideShowProspectDetailsModalFromHook();
  }, [hideShowProspectDetailsModalFromHook]);

  const handleLeadClick = useCallback(
    (leadId: number) => {
      if (!hasPermission(Permissions.PROSPECT_READ)) {
        return;
      }
      openProspectDetailsModal(leadId);
    },
    [openProspectDetailsModal],
  );

  const handleRevealEmail = useCallback(
    (leadId: number) => {
      // "View Email" in the Work Email column always reveals work email
      // (LeadRevealType.Email, 1 credit), regardless of the user's
      // default-email-reveal-type setting. Personal email has its own
      // dedicated column + handler (handleRevealPersonalEmail).
      if (Number(leadCredits) < 1) {
        dispatch(openCreditNotAvailableModal(LeadRevealType.Email));
        // Track analytics
        window.analytics?.track?.({
          userId: getUserTrackingId(),
          event: AnalyticsEvents.TrippedFeature,
          properties: {
            shAccountId: getUserShAccountId(),
            current_plan: getUserPlanName(),
            role: getUserRole(),
            'LF Plan': getUserLeadFinderPlanName(),
            feature_type: 'Lead Finder',
          },
        });
        return;
      }
      setEmailRevealingLeads((prev) => ({
        ...prev,
        [leadId]: true,
      }));
      // Call reveal - the state update will trigger a re-render
      handleRevealLead(leadId, LeadRevealType.Email);
    },
    [handleRevealLead, leadCredits, setEmailRevealingLeads, dispatch],
  );

  const handleRevealEmailPhone = useCallback(
    (leadId: number) => {
      // Credit check for single lead reveal
      const isPersonalEmailDefault =
        defaultEmailRevealType === 'personal_email';
      const emailPhoneMinCredits = isPersonalEmailDefault ? 9 : 7;
      if (Number(leadCredits) < emailPhoneMinCredits) {
        dispatch(
          openCreditNotAvailableModal(
            isPersonalEmailDefault
              ? LeadRevealType.EmailPersonalEmailPhone
              : LeadRevealType.EmailPhone,
          ),
        );
        // Track analytics
        window.analytics?.track?.({
          userId: getUserTrackingId(),
          event: AnalyticsEvents.TrippedFeature,
          properties: {
            shAccountId: getUserShAccountId(),
            current_plan: getUserPlanName(),
            role: getUserRole(),
            'LF Plan': getUserLeadFinderPlanName(),
            feature_type: 'Lead Finder',
          },
        });
        return;
      }
      setEmailPhoneRevealingLeads((prev) => ({
        ...prev,
        [leadId]: true,
      }));
      const phoneRevealType =
        defaultEmailRevealType === 'personal_email'
          ? LeadRevealType.EmailPersonalEmailPhone
          : LeadRevealType.EmailPhone;
      // When default email type is personal_email, the phone reveal also
      // reveals personal email — set its local map so the column shows
      // "Enriching" immediately without relying on emailPhoneRevealingLeads.
      if (defaultEmailRevealType === 'personal_email') {
        setPersonalEmailRevealingLeads((prev) => ({
          ...prev,
          [leadId]: true,
        }));
      }
      handleRevealLead(leadId, phoneRevealType);
    },
    [
      handleRevealLead,
      leadCredits,
      defaultEmailRevealType,
      setEmailPhoneRevealingLeads,
      setPersonalEmailRevealingLeads,
      dispatch,
    ],
  );

  const handleRevealPersonalEmail = useCallback(
    (leadId: number) => {
      // Credit check for personal email reveal (costs 2 credits)
      if (Number(leadCredits) < 2) {
        dispatch(openCreditNotAvailableModal(LeadRevealType.PersonalEmail));
        window.analytics?.track?.({
          userId: getUserTrackingId(),
          event: AnalyticsEvents.TrippedFeature,
          properties: {
            shAccountId: getUserShAccountId(),
            current_plan: getUserPlanName(),
            role: getUserRole(),
            'LF Plan': getUserLeadFinderPlanName(),
            feature_type: 'Lead Finder',
          },
        });
        return;
      }
      setPersonalEmailRevealingLeads((prev) => ({
        ...prev,
        [leadId]: true,
      }));
      handleRevealLead(leadId, LeadRevealType.PersonalEmail);
    },
    [handleRevealLead, leadCredits, setPersonalEmailRevealingLeads, dispatch],
  );

  const handleSaveAsProspect = useCallback(
    async (leadId: number) => {
      try {
        setLoadingAddToProspect((prev) => ({ ...prev, [leadId]: true }));
        const addToProspectRes = await leadAddToProspectAPI(leadId);
        if (addToProspectRes) {
          handleAppendLead(addToProspectRes?.payload, true);
          toaster.success(
            LEAD_LISTING_MESSAGES.add_to_prospect_single_lead_succeess_msg,
          );
          dispatch(setSelectedLeads([]));
          dispatch(setIsSelectedAllLeads(false));
          dispatch(setDeSelectedLeads([]));
        }

        dispatch(getPostLoadMetaRequest());
        setLoadingAddToProspect((prev) => {
          const updated = { ...prev };
          delete updated[leadId];
          return updated;
        });
      } catch (e: any) {
        setLoadingAddToProspect((prev) => {
          const updated = { ...prev };
          delete updated[leadId];
          return updated;
        });
        if (!e.isHandled && e.message) {
          toaster.error(e.message);
        }
      }
    },
    [handleAppendLead, dispatch],
  );

  const handleAddToSequence = useCallback(
    (leadId: number) => {
      dispatch(openAddToSequenceModal(leadId));
    },
    [dispatch],
  );

  const handleBulkAction = useCallback(
    (actionType: BulkLeadsActionType) => {
      const leadIds = selectedLeads;
      const leadFinderLimit =
        activeSubTab === LeadFinderSecondaryTab.SAVED
          ? SAVED_TAB_BULK_OPERATION_LIMIT
          : leadFinderBulkOperationLimit();

      switch (actionType) {
        case BulkLeadsActionType.REVEAL_EMAIL:
          handleBulkRevealWithTags(
            defaultEmailRevealType === 'personal_email'
              ? LeadRevealType.PersonalEmail
              : LeadRevealType.Email,
          );
          break;
        case BulkLeadsActionType.REVEAL_EMAIL_PHONE:
          handleBulkRevealWithTags(
            defaultEmailRevealType === 'personal_email'
              ? LeadRevealType.EmailPersonalEmailPhone
              : LeadRevealType.EmailPhone,
          );
          break;
        case BulkLeadsActionType.ADD_TO_PROSPECT:
          if (leadIds?.length > leadFinderLimit) {
            toaster.error(
              `Please select less than ${leadFinderLimit} leads to proceed!`,
            );
            return;
          }
          handleBulkActionFromHook(actionType);
          break;
        case BulkLeadsActionType.ADD_TO_SEQUENCE:
          if (leadIds?.length > leadFinderLimit) {
            toaster.error(
              `Please select less than ${leadFinderLimit} leads to proceed!`,
            );
            return;
          }
          handleBulkActionFromHook(actionType);
          break;
        case BulkLeadsActionType.EXPORT:
          if (isDemoAccount) {
            dispatch(setShowGlobalRestrictionModal(true));
            return;
          }

          if (activeLeadType === LeadFinderPrimaryTab.PEOPLE) {
            if (leadIds?.length > leadFinderLimit) {
              toaster.error(
                `Please select less than ${leadFinderLimit} leads to proceed!`,
              );
              return;
            }
            handleBulkActionFromHook(actionType);
          } else if (activeLeadType === LeadFinderPrimaryTab.COMPANY) {
            handleCompanyExport();
          }
          break;
        default:
          break;
      }
    },
    [
      selectedLeads,
      activeSubTab,
      activeLeadType,
      handleBulkActionFromHook,
      handleBulkRevealWithTags,
      handleCompanyExport,
      defaultEmailRevealType,
    ],
  );

  const hideAddToSequenceModal = useCallback(() => {
    dispatch(closeAddToSequenceModal());
  }, [dispatch]);

  const handleRemoveFromListModalClose = useCallback(() => {
    dispatch(setShowRemoveFromListModal(false));
    setLeadIdToAddFromRow(null);
    leadIdToAddFromRowRef.current = null;
  }, [dispatch, setLeadIdToAddFromRow, leadIdToAddFromRowRef]);

  const handleRequestOpenRemoveFromList = useCallback(() => {
    dispatch(setShowRemoveFromListModal(true));
  }, [dispatch]);

  const handleCreateNewList = useCallback(() => {
    // TODO: Open create list modal
  }, []);

  const handleBulkRevealTagModalClose = useCallback(() => {
    dispatch(setShowBulkRevealTagModal(false));
    setTagsToAdd([]);
    setLeadRevealType(null);
  }, [dispatch, setTagsToAdd, setLeadRevealType]);

  const handleCreditModalHide = useCallback(() => {
    dispatch(closeCreditNotAvailableModal());
  }, [dispatch]);

  const handleCreditModalAddCredits = useCallback(() => {
    redirectWithoutRefresh(Routes.Setting_Billing_Add_Credits);
    dispatch(closeCreditNotAvailableModal());
  }, [dispatch]);

  const handleSelectedMoreThanLimitModalClose = useCallback(() => {
    dispatch(setShowSelectedMoreThanLimitModal(false));
  }, [dispatch]);

  const handleWhatsNewModalClose = useCallback(() => {
    setShowWhatsNewModal(false);
  }, []);

  const handleAiChatApplyUrl = useCallback(
    (url: string) => {
      try {
        const parsed = new URL(url);
        const params = parsed.searchParams;
        const targetTab = parsed.pathname.includes('/company/')
          ? LeadFinderPrimaryTab.COMPANY
          : LeadFinderPrimaryTab.PEOPLE;

        // Store current tab's filters before navigating (mirrors handleLeadTypeChange logic)
        const currentParams = new URLSearchParams(location.search);
        const currentTabKey = location.pathname.includes('/v2/leads/company/')
          ? 'company'
          : 'people';
        const currentFilters = getAllFiltersExceptPagination(currentParams);
        tabSpecificFiltersRef.current[
          currentTabKey
        ] = currentFilters.toString();

        history.push(
          `/v2/leads/${targetTab}/${
            LeadFinderSecondaryTab.TOTAL
          }?${params.toString()}`,
        );
      } catch (_) {
        // If URL parsing fails, ignore
      }
    },
    [
      history,
      location.search,
      location.pathname,
      getAllFiltersExceptPagination,
    ],
  );

  const handleSetSidebarExpanded = useCallback(
    (value: boolean) => {
      dispatch(setSidebarExpanded(value));
    },
    [dispatch],
  );

  const handleSendGetTeamsListRequest = useCallback(() => {
    dispatch(
      getTeamsRequest({
        pageNum: 1,
        pageSize: 500,
      }),
    );
  }, [dispatch]);

  const addContactToStep = useCallback(
    async (sequenceId: number, stepId: number) => {
      const leadId = selectedLeadIdForSequence;
      if (!leadId) return;

      try {
        setLoadingAddToSequence((prev) => ({ ...prev, [leadId]: true }));
        const response = await leadAddToSequenceAPI({
          leadId,
          sequenceId,
          stepId,
        });
        if (response?.payload?.failed) {
          if (response?.payload?.failedReport?.length) {
            const failedReport = response?.payload?.failedReport?.[0];
            const errorMessage =
              failedReport?.reason ||
              'Prospect already exists in the selected step';
            toaster.error(errorMessage, {
              theme: Theme.New,
            });
          } else {
            toaster.error('Prospect already exists in the selected step', {
              theme: Theme.New,
            });
          }
        } else if (response?.payload?.isAddedToSequence) {
          toaster.success(
            LEAD_LISTING_MESSAGES.add_to_sequence_single_lead_succeess_msg,
          );
        }
        dispatch(setSelectedLeads([]));
        dispatch(setIsSelectedAllLeads(false));
        dispatch(setDeSelectedLeads([]));

        if (response?.payload?.isContactCreated) {
          setLeadsData((prev) => ({
            ...prev,
            profiles: prev.profiles?.map((lead) =>
              lead.id === leadId ? { ...lead, isProspectCreated: true } : lead,
            ),
          }));
        }
        dispatch(getPostLoadMetaRequest());
      } catch (e: any) {
        if (!e.isHandled && e.message) {
          toaster.error(e.message);
        }
      } finally {
        setLoadingAddToSequence((prev) => {
          const updated = { ...prev };
          delete updated[leadId];
          return updated;
        });
      }
    },
    [selectedLeadIdForSequence, dispatch],
  );

  const handlePaginationChange = useCallback(
    (page: number, limit: number) => {
      const urlParams = new URLSearchParams(window.location.search);
      urlParams.delete('page');
      urlParams.delete('start');
      urlParams.delete('take');
      const filterSource =
        !urlParams.toString() && tabSpecificFiltersRef.current.people
          ? tabSpecificFiltersRef.current.people
          : undefined;
      handlePagination({ page, limit, filterSource });
    },
    [handlePagination, tabSpecificFiltersRef],
  );

  const addToListData = useMemo(() => {
    if (activeLeadType === LeadFinderPrimaryTab.COMPANY) {
      return (companyListsData || []).map((list: any) => ({
        id: list.id,
        label: list.label,
        visibility: list.visibility,
        owner: list.owner,
        isDefault: list.isDefault,
      }));
    }
    return peopleListsData || [];
  }, [activeLeadType, companyListsData, peopleListsData]);

  const addToListModalProps = useMemo(
    () => ({
      teamsList,
      getTeamsListRequestStatus,
      sendGetTeamsListRequest: handleSendGetTeamsListRequest,
      onSaveList: handleSaveList,
      isUpdateListLoading: isCreateListLoading,
    }),
    [
      teamsList,
      getTeamsListRequestStatus,
      handleSendGetTeamsListRequest,
      handleSaveList,
      isCreateListLoading,
    ],
  );

  const leadsPagination = useMemo(() => {
    if (countsError && activeSubTab !== LeadFinderSecondaryTab.SAVED) {
      return undefined;
    }
    return {
      page: leadsData?.pagination?.page || 1,
      limit: leadsData?.pagination?.limit || 25,
      totalRows: leadsData?.pagination?.totalRows || 0,
    };
  }, [
    countsError,
    activeSubTab,
    leadsData?.pagination?.page,
    leadsData?.pagination?.limit,
    leadsData?.pagination?.totalRows,
  ]);

  // On the saved tab the pagination total comes from the saved-counts API, which
  // can resolve after the leads list. Keep the table in its skeleton state until
  // the saved count is available so the footer never flashes a wrong total.
  const isLeadsTableLoading = useMemo(() => {
    const isSavedSubTab = activeSubTab === LeadFinderSecondaryTab.SAVED;
    if (isSavedSubTab) {
      const savedCountReady = countsData?.revealedCount !== undefined;
      return isLoading || (isLoadingSavedCounts && !savedCountReady);
    }
    return isLoading;
  }, [isLoading, isLoadingSavedCounts, countsData?.revealedCount, activeSubTab]);

  const handleCompanyFeedbackUpdate = useCallback(
    (
      companyId: number,
      sentiment: LeadRevealFeedbackSentiment,
      message?: string,
    ) => {
      setCompaniesData((prev) => {
        if (!prev?.profiles) return prev;
        const targetId = Number(companyId);
        const next = prev.profiles.map((c) => {
          const cid = c.id ?? c.company_id;
          if (cid == null || Number(cid) !== targetId) return c;
          return {
            ...c,
            revealFeedback: {
              sentiment,
              ...(message ? { message } : {}),
            },
          };
        });
        return { ...prev, profiles: next };
      });
    },
    [setCompaniesData],
  );

  const companyPagination = useMemo(() => {
    if (companyCountsError && activeSubTab !== LeadFinderSecondaryTab.SAVED) {
      return undefined;
    }
    return {
      page: companiesData?.pagination?.page || 1,
      limit: companiesData?.pagination?.limit || 25,
      totalRows: companiesData?.pagination?.totalRows || 0,
    };
  }, [
    companyCountsError,
    activeSubTab,
    companiesData?.pagination?.page,
    companiesData?.pagination?.limit,
    companiesData?.pagination?.totalRows,
  ]);

  const mergedTagButtonProps = useMemo(
    () =>
      tagButtonProps
        ? ({
            ...tagButtonProps,
            triggerOpenAddTag,
            triggerOpenRemoveTag,
            onRowTagModalClosed: handleRowTagModalClosed,
            overrideSelectedLeadObjects,
            onRefreshSavedCount: refreshPeopleSavedCounts,
          } as any)
        : undefined,
    [
      tagButtonProps,
      triggerOpenAddTag,
      triggerOpenRemoveTag,
      handleRowTagModalClosed,
      overrideSelectedLeadObjects,
      refreshPeopleSavedCounts,
    ],
  );

  const mergedCompanyTagButtonProps = useMemo(
    () =>
      companyTagButtonProps
        ? {
            ...companyTagButtonProps,
            triggerOpenAddTag,
            triggerOpenRemoveTag,
            renderWhenTriggered: true,
            onRowTagModalClosed: handleRowTagModalClosed,
            overrideSelectedCompanyObjects,
          }
        : undefined,
    [
      companyTagButtonProps,
      triggerOpenAddTag,
      triggerOpenRemoveTag,
      handleRowTagModalClosed,
      overrideSelectedCompanyObjects,
    ],
  );

  const isTagsFilterApplied = useMemo(
    () => getTagsFilterApplied({ prospectsFilters }),
    [prospectsFilters],
  );

  // Determine which screen to show
  // For company tab, check companiesData; for people tab, check leadsData
  const currentData = isCompanyTab ? companiesData : leadsData;
  const currentIsLoading = isCompanyTab ? isCompanyLoading : isLoading;
  const storedUrls = isCompanyTab ? storedUrlsCompany : storedUrlsPeople;

  // Show when user hasn't searched yet (no API response with pagination)
  const showSearchingEmpty =
    !currentIsLoading &&
    activeSubTab !== LeadFinderSecondaryTab.SAVED &&
    storedUrls.length === 0 &&
    !location.search &&
    !currentData?.pagination;

  const showRecentSearch =
    !currentIsLoading &&
    activeSubTab !== LeadFinderSecondaryTab.SAVED &&
    storedUrls &&
    storedUrls.length > 0 &&
    !location.search &&
    !currentData?.pagination &&
    (!currentData?.profiles || currentData?.profiles?.length === 0);

  // Show when search API returned 0 results (works for URL-based and bulk search)
  const showNoResults =
    !currentIsLoading &&
    currentData &&
    currentData?.pagination &&
    (!currentData?.profiles || currentData?.profiles?.length === 0) &&
    currentData?.pagination?.totalRows === 0 &&
    !(activeSubTab === LeadFinderSecondaryTab.SAVED && !location.search);

  // Show table when loading OR when there are leads/companies (includes bulk search without URL)
  const showLeadsTable =
    currentIsLoading ||
    (currentData?.profiles &&
      currentData?.profiles?.length > 0 &&
      (location.search ||
        activeSubTab === LeadFinderSecondaryTab.SAVED ||
        (currentData?.pagination && currentData?.pagination?.totalRows > 0)));

  // For saved tab: show empty state when no leads/companies are saved and no search is applied
  const showSavedTabEmpty =
    !currentIsLoading &&
    activeSubTab === LeadFinderSecondaryTab.SAVED &&
    currentData?.pagination?.totalRows === 0 &&
    !location.search;

  // Don't render if API succeeded and user doesn't have access
  const shouldBlockAccessRender =
    getIsRequestSucceeded(getUserSettingsRequestStatus) &&
    !hasV2LeadFinderAccess;

  if (shouldBlockAccessRender) {
    return null;
  }

  return (
    <Container
      fluid
      className={classNames('v2-lead-container px-0', {
        'v2-lead-container--with-demo-banner': hasDemoAccountBanner,
      })}
    >
      <ErrorBoundaryWrapper>
        <Suspense>
          <LeadFinderExtensionBanner />
          <V2LeadHeader
            activeTab={activeLeadType}
            activeSubTab={activeSubTab}
            onTabChange={handleLeadTypeChange}
            isLoading={isLoading}
            leadCredits={leadCredits}
            selectedLeadsCount={selectedLeads.length}
            selectedLeads={selectedLeads}
            isSelectedAllLeads={isSelectedAllLeads}
            onBulkAction={handleBulkAction}
            selectedCompaniesCount={selectedCompanies.length}
            selectedCompanies={selectedCompanies}
            isSelectedAllCompanies={isSelectedAllCompanies}
            companiesData={companiesData}
            leadsData={leadsData}
            onCompanyBulkFindPeople={handleBulkFindPeople}
            isLoadingBulkFindPeople={isBulkFindPeopleLoading}
            addToListData={addToListData}
            addToListOpen={addToListOpenFromRow}
            onAddToListOpenChange={handleAddToListOpenChange}
            addFromRowCompanyId={companyIdToAddFromRow}
            addFromRowLeadId={leadIdToAddFromRow}
            onAddToList={handleAddToList}
            onRemoveFromList={handleRemoveFromList}
            onCreateNewList={handleCreateNewList}
            addToListModalProps={addToListModalProps}
            loadingBulkAction={loadingBulkAction}
            loadingRemoveFromList={loadingRemoveFromList}
            showRemoveFromListModal={showRemoveFromListModal}
            onRemoveFromListModalClose={handleRemoveFromListModalClose}
            onRequestOpenRemoveFromList={handleRequestOpenRemoveFromList}
            tagButtonProps={mergedTagButtonProps}
            companyTagButtonProps={mergedCompanyTagButtonProps}
            defaultEmailRevealType={defaultEmailRevealType}
          />
          <Row className="m-0">
            {!isCsvEnrichmentTab && (
              <V2LeadSidebar
                isSidebarExpanded={isSidebarExpanded}
                setIsSidebarExpanded={handleSetSidebarExpanded}
                handleRecentSearch={handleRecentSearchWithRefresh}
                handleGeneratePayload={
                  handlePeopleGeneratePayloadWithBulkFallback
                }
                setSelectedLeads={handleSidebarSetSelectedLeads}
                showPhoneNumberNudge={showPhoneNumberNudge}
                setShowPhoneNumberNudge={setShowPhoneNumberNudge}
                setIgnorePhoneNumberNudge={setIgnorePhoneNumberNudge}
                searchLeads={searchLeads}
                isLoading={isLoading}
                isLoadingCounts={isLoadingCounts}
                isLoadingSavedCounts={isLoadingSavedCounts}
                keywordInputRef={keywordInputRef}
                activeSubTab={activeSubTab}
                onFilterTabChange={handleSubTabChange}
                leadTags={leadTags}
                isLeadTagsLoading={isLeadTagsLoading}
                savedFilters={savedFilters}
                setSavedFilters={setSavedFilters}
                countsData={countsData}
                countsError={countsError}
                handleRouteChange={handleRouteChange}
                // Company-specific props
                activeLeadType={activeLeadType}
                searchCompanies={isCompanyTab ? searchCompanies : undefined}
                setSelectedCompanies={handleSidebarSetSelectedCompanies}
                companyCountsData={companyCountsData}
                companyCountsError={companyCountsError}
                isCompanyLoading={isCompanyLoading}
                isLoadingCompanyCounts={isLoadingCompanyCounts}
                isLoadingCompanySavedCounts={isLoadingCompanySavedCounts}
                handleCompanyGeneratePayload={handleCompanyGeneratePayload}
                companyListsData={companyListsData}
                peopleTabCompanyListsData={peopleTabCompanyListsData}
                isLoadingCompanyLists={isLoadingCompanyLists}
                isLoadingPeopleLists={isLoadingPeopleLists}
                peopleListsData={peopleListsData}
                onSaveList={handleSaveList}
                onCreateListOnly={handleCreateListOnly}
                isUpdateListLoading={isCreateListLoading}
                onExportList={handleExportList}
                onDeleteList={handleDeleteList}
                isDeleteListLoading={isDeleteListLoading}
                teamsList={teamsList}
                getTeamsListRequestStatus={getTeamsListRequestStatus}
                sendGetTeamsListRequest={handleSendGetTeamsListRequest}
                hasPaymentPendingBanner={hasPaymentPendingBanner}
                hasDemoAccountBanner={hasDemoAccountBanner}
                signalsListData={signalsListData}
                newsListData={newsListData}
                handleCompanyRecentSearch={handleCompanyRecentSearchWithRefresh}
                loginUserEmail={loginUserEmail}
                triggerSearchWithFilters={
                  isCompanyTab
                    ? handleCompanyTriggerSearchWithFilters
                    : handlePeopleTriggerSearchWithFilters
                }
                onClearAll={
                  isCompanyTab ? handleClearAllCompany : handleClearAllPeople
                }
                skipUrlPushForBulkSearchRef={skipUrlPushForBulkSearchRef}
                tabSpecificFiltersRef={tabSpecificFiltersRef}
                tabSpecificBulkModeRef={tabSpecificBulkModeRef}
                syncCompanyTabSpecificFiltersRef={
                  syncCompanyTabSpecificFiltersRef
                }
                syncPeopleTabSpecificFiltersRef={
                  syncPeopleTabSpecificFiltersRef
                }
                companyFilterRestoreRef={companyFilterRestoreRef}
                peopleFilterRestoreRef={peopleFilterRestoreRef}
              />
            )}

            <Col className={mainContentClass}>
              {/* CSV Enrichment tab content */}
              {isCsvEnrichmentTab && <CsvEnrichmentContainer />}
              {/* Show empty state when no search history */}
              {!isCsvEnrichmentTab && showSearchingEmpty && <SearchingEmpty />}

              {/* Show recent searches */}
              {!isCsvEnrichmentTab && showRecentSearch && (
                <RecentSearch
                  storedUrls={storedUrls}
                  setStoredUrls={
                    isCompanyTab ? setStoredUrlsCompany : setStoredUrlsPeople
                  }
                  loginUserEmail={loginUserEmail}
                  searchLeads={searchLeads}
                  searchCompanies={searchCompanies}
                  onRestoreCompanyFilterUI={onRestoreCompanyFilterUI}
                  onRestorePeopleFilterUI={onRestorePeopleFilterUI}
                  onFetchPeopleCounts={handleFetchCounts}
                  storageKey={
                    isCompanyTab
                      ? V2_LEAD_FINDER_RECENT_SEARCHES_STORAGE_KEY_COMPANY
                      : V2_LEAD_FINDER_RECENT_SEARCHES_STORAGE_KEY
                  }
                  companyListsData={companyListsData}
                  peopleListsData={peopleListsData}
                />
              )}

              {/* Show no results for total/new tabs, and for saved tab when a search is applied */}
              {!isCsvEnrichmentTab && showNoResults && (
                <div className="v2-no-search-lead">
                  <NoSearchResult
                    description={noResultsDescription}
                    title="No results found"
                  >
                    <NoSearchResultIcon />
                  </NoSearchResult>
                </div>
              )}

              {/* Show empty state for saved tab */}
              {!isCsvEnrichmentTab && showSavedTabEmpty && (
                <div className="v2-no-search-lead">
                  <NoSearchResult
                    description={noSavedLeadsDescription}
                    title="No saved leads"
                  >
                    <NoSearchResultIcon />
                  </NoSearchResult>
                </div>
              )}

              {/* Show leads table - conditionally render People or Company table */}
              <Suspense
                fallback={
                  <TableLoadingSkeleton
                    tab={isCompanyTab ? 'companies' : 'leads'}
                  />
                }
              >
                {!isCsvEnrichmentTab && showLeadsTable && !isCompanyTab && (
                  <div className="v2-lead-table-wrapper">
                    <V2LeadTable
                      leads={leadsData?.profiles || []}
                      isLoading={isLeadsTableLoading}
                      selectedLeadIds={selectedLeads}
                      leadCredits={leadCredits}
                      onLeadSelect={handleLeadSelect}
                      onLeadSelectAll={handleLeadSelectAll}
                      onLeadClick={handleLeadClick}
                      pagination={leadsPagination}
                      onPaginationChange={handlePaginationChange}
                      onRevealEmail={handleRevealEmail}
                      onRevealEmailPhone={handleRevealEmailPhone}
                      onRevealPersonalEmail={handleRevealPersonalEmail}
                      onSaveAsProspect={handleSaveAsProspect}
                      onAddToSequence={handleAddToSequence}
                      emailRevealingLeads={emailRevealingLeads}
                      emailPhoneRevealingLeads={emailPhoneRevealingLeads}
                      personalEmailRevealingLeads={personalEmailRevealingLeads}
                      defaultEmailRevealType={defaultEmailRevealType}
                      loadingAddToProspect={loadingAddToProspect}
                      loadingAddToSequence={loadingAddToSequence}
                      activeSubTab={activeSubTab}
                      onSelectAllAcrossPages={handleSelectAllAcrossPages}
                      isSelectedAllLeads={isSelectedAllLeads}
                      selectAllRevealType={selectAllRevealType}
                      onRowAddToList={handleSingleLeadAddToList}
                      onRowRemoveFromList={handleLeadRowRemoveFromList}
                      onRowAddTag={handleLeadRowAddTag}
                      onRowRemoveTag={handleLeadRowRemoveTag}
                      hasListPermission={
                        hasPermission(Permissions.ACCOUNT_USER_READ) ||
                        hasPermission(Permissions.TEAM_USER_READ)
                      }
                      onRevealCompany={handleRevealCompanyWithCreditCheck}
                      enrichingCompanies={enrichingCompanies}
                      companiesProfiles={companiesData?.profiles}
                    />
                  </div>
                )}

                {/* Show company table when Company tab is active */}
                {!isCsvEnrichmentTab &&
                  isCompanyTab &&
                  (isCompanyLoading ||
                    (companiesData?.profiles &&
                      companiesData?.profiles?.length > 0 &&
                      (location.search ||
                        activeSubTab === LeadFinderSecondaryTab.SAVED ||
                        (companiesData?.pagination &&
                          companiesData?.pagination?.totalRows > 0)))) && (
                    <div className="v2-lead-table-wrapper">
                      <V2CompanyTable
                        companies={companiesData?.profiles || []}
                        isLoading={isCompanyLoading}
                        selectedCompanyIds={selectedCompanies}
                        leadCredits={leadCredits}
                        onRevealCompany={handleRevealCompanyWithCreditCheck}
                        enrichingCompanies={enrichingCompanies}
                        onCompanySelect={handleCompanySelect}
                        onCompanySelectAll={handleCompanySelectAll}
                        onSelectAllAcrossPages={handleSelectAllAcrossPages}
                        isSelectedAllCompanies={isSelectedAllCompanies}
                        pagination={companyPagination}
                        onPaginationChange={handleCompanyPaginationChange}
                        activeSubTab={activeSubTab}
                        onSaveCompany={handleSaveCompanyFromRow}
                        onFindPeople={handleFindPeople}
                        onRowAddToList={handleRowAddToList}
                        onRowRemoveFromList={handleRowRemoveFromList}
                        onRowAddTag={handleRowAddTag}
                        onRowRemoveTag={handleRowRemoveTag}
                        hasListPermission={
                          hasPermission(Permissions.ACCOUNT_USER_READ) ||
                          hasPermission(Permissions.TEAM_USER_READ)
                        }
                        onCompanyFeedbackUpdate={handleCompanyFeedbackUpdate}
                      />
                    </div>
                  )}
              </Suspense>
            </Col>
          </Row>
        </Suspense>
      </ErrorBoundaryWrapper>

      {showAddToSequenceModal && (
        <Suspense>
          <AddToSequenceModal
            show={showAddToSequenceModal}
            onHide={hideAddToSequenceModal}
            addToSequence={
              selectedLeadIdForSequence
                ? addContactToStep
                : handleBulkAddToSequence
            }
            isSubscribed={true}
          />
        </Suspense>
      )}

      {showProspectDetailsModal && selectedContactId && (
        <Suspense>
          <ProspectDetails
            show={showProspectDetailsModal}
            onHide={hideShowProspectDetailsModal}
            selectedProspectId={selectedContactId}
            resetSelectedProspectId={hideShowProspectDetailsModal}
            selectedProspectData={singleProspect}
            selectedProspectSequences={singleProspectSequenceDetails}
            getSingleProspectDetailsStatus={
              getSingleProspectDetailsRequest.status
            }
            getSingleProspectSequencesDetailsStatus={
              getSingleProspectSequencesDetailsRequest.status
            }
            isTagsFilterApplied={isTagsFilterApplied}
            defaultTab={defaultTab}
          />
        </Suspense>
      )}

      {showBulkRevealTagModal && (
        <Suspense>
          <BulkRevealTagModal
            show={showBulkRevealTagModal}
            onClose={handleBulkRevealTagModalClose}
            onSubmit={handleBulkRevealSubmit}
            isLoading={isBulkRevealing}
            effectiveLeadsCount={bulkRevealEffectiveCount}
            leadTags={leadTags}
            tagsToAdd={tagsToAdd}
            setTagsToAdd={setTagsToAdd}
          />
        </Suspense>
      )}

      {/* Credit Not Available Modal */}
      {showCreditNotAvailableModal && creditCheckRevealType && (
        <Suspense>
          <CreditNotAvailableModal
            show={showCreditNotAvailableModal}
            onHide={handleCreditModalHide}
            onAddCredits={handleCreditModalAddCredits}
            revealType={creditCheckRevealType}
          />
        </Suspense>
      )}

      {/* Selected More Than Limit Modal */}
      {showSelectedMoreThanLimitModal && (
        <Suspense>
          <SelectedMoreThanLimitModal
            show={showSelectedMoreThanLimitModal}
            onClose={handleSelectedMoreThanLimitModalClose}
          />
        </Suspense>
      )}

      {/* What's New Modal - shown when user switches from old to new lead finder */}
      {showWhatsNewModal && (
        <Suspense>
          <WhatsNewModal
            show={showWhatsNewModal}
            onClose={handleWhatsNewModalClose}
          />
        </Suspense>
      )}

      {showAiSearchPopup && (
        <Suspense>
          <AiSearchPopup
            show={showAiSearchPopup}
            onClose={() => dispatch(setShowAiSearchPopup(false))}
          />
        </Suspense>
      )}
      <Suspense>
        <GlobalRestrictionModal
          show={showGlobalRestrictionModal}
          onClose={() => dispatch(setShowGlobalRestrictionModal(false))}
          title="Exporting data is disabled in demo account"
          content="This is a demo account with sample data. Signup to start exporting the data"
        />
      </Suspense>
      {!showAiChatPanel &&
        !showSearchingEmpty &&
        !showRecentSearch &&
        !isCsvEnrichmentTab && (
          <button
            type="button"
            className="ai-chat-fab"
            onClick={() => dispatch(openAiChatPanel())}
            title="Sage"
          >
            <NoResultGradientImage />
          </button>
        )}
      <Suspense>
        <AiChatPanel
          show={showAiChatPanel}
          onClose={() => dispatch(closeAiChatPanel())}
          onApplyUrl={handleAiChatApplyUrl}
          pendingQuery={aiChatPendingQuery}
          onPendingQueryConsumed={() => dispatch(clearAiChatPendingQuery())}
          onConversationIdChange={(id) =>
            dispatch(setActiveChatConversationId(id))
          }
          initialView={aiChatInitialView}
          onInitialViewConsumed={() => dispatch(resetAiChatInitialView())}
        />
      </Suspense>
    </Container>
  );
};

export default V2Lead;
